/**
 * FTA Settings Reader — typed interface to user preferences in localStorage.
 * Single source of truth for solver selection, AI level, and feature toggles.
 */

export type SolverType = 'builtin' | 'scram-cli' | 'scram-service';
export type AILevel = 'manual' | 'assisted' | 'augmented' | 'full-pipeline';

export interface FTASettings {
  solver: SolverType;
  aiLevel: AILevel;
  enableDSR: boolean;
  enablePreReasoning: boolean;
  enableScorePredict: boolean;
  scramPath: string;
  scramServiceUrl: string;
  missionTime: number;
  programName: string;
}

const DEFAULTS: FTASettings = {
  solver: 'builtin', aiLevel: 'augmented', enableDSR: true,
  enablePreReasoning: true, enableScorePredict: true,
  scramPath: '/usr/local/bin/scram', scramServiceUrl: 'http://localhost:8080/scram',
  missionTime: 1, programName: 'Program',
};

export function readFTASettings(): FTASettings {
  try {
    const raw = localStorage.getItem('sn-settings');
    if (!raw) return DEFAULTS;
    const p = JSON.parse(raw);
    return {
      solver: ['builtin','scram-cli','scram-service'].includes(p.solver) ? p.solver : DEFAULTS.solver,
      aiLevel: ['manual','assisted','augmented','full-pipeline'].includes(p.aiLevel) ? p.aiLevel : DEFAULTS.aiLevel,
      enableDSR: typeof p.enableDSR === 'boolean' ? p.enableDSR : DEFAULTS.enableDSR,
      enablePreReasoning: typeof p.enablePreReasoning === 'boolean' ? p.enablePreReasoning : DEFAULTS.enablePreReasoning,
      enableScorePredict: typeof p.enableScorePredict === 'boolean' ? p.enableScorePredict : DEFAULTS.enableScorePredict,
      scramPath: p.scramPath || DEFAULTS.scramPath,
      scramServiceUrl: p.scramServiceUrl || DEFAULTS.scramServiceUrl,
      missionTime: typeof p.missionTime === 'number' ? p.missionTime : DEFAULTS.missionTime,
      programName: p.programName || DEFAULTS.programName,
    };
  } catch { return DEFAULTS; }
}

export interface FeatureVisibility {
  showAIGenerate: boolean;
  showAnalysisMenu: boolean;
  showChecksMenu: boolean;
  showDSRPanel: boolean;
  showCalibration: boolean;
  showCriticPanel: boolean;
}

export function getFeatureVisibility(s: FTASettings): FeatureVisibility {
  const ai = s.aiLevel;
  return {
    showAIGenerate: ai === 'augmented' || ai === 'full-pipeline',
    showAnalysisMenu: ai !== 'manual',
    showChecksMenu: ai !== 'manual',
    showCriticPanel: ai === 'augmented' || ai === 'full-pipeline',
    showDSRPanel: ai === 'full-pipeline' && s.enableDSR,
    showCalibration: ai === 'full-pipeline',
  };
}

export function getSolverLabel(s: SolverType): string {
  return s === 'builtin' ? 'Built-in BDD/DPC' : s === 'scram-cli' ? 'SCRAM CLI' : 'SCRAM Service';
}
